/**
 * Soft limit ~80%/100%: estructural (sucursales/usuarios) y/o volumen mensual (Cap B).
 * Nunca bloquea caja/POS — solo aviso → Facturación (ahí se comparan y eligen planes).
 * Meter + copy "Te quedan ~N <métrica> este mes" (proto billing-activar-crece-vs-hoy-v1).
 * Estados: volume near / volume over (celebración + fine print) / structural
 * (en el tope = pitch del tope mensual de volumen, no warning) / legacy volume
 * (agradecimiento).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import {
  creceLegacyVolumeCopy,
  creceSoftLimitCopy,
  creceVolumeSoftLimitCopy,
  emprendeVolumeSoftLimitCopy,
} from '../../../../../../shared/plan-crece-copy';
import { usePlanEntitlements } from '../../providers/PlanEntitlementsProvider';
import { navigateInShell } from '../../navigation/rootNavigation';
import { isBillingEnabled } from '../isBillingEnabled';
import { spacing } from '../../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { trackCreceFunnel } from '../trackCreceFunnel';
import { CRECE_FUNNEL_EVENTS } from '../../../../../../shared/crece-funnel';
import { resolveSubscribedPaidTier } from '../billingScreen.utils';
import {
  dismissSoftLimit,
  isSoftLimitDismissed,
  type SoftLimitKind,
} from '../softLimitDismissal';
import {
  activeVolumeCaps,
  activeVolumeMetrics,
  hottestStructuralUsage,
  hottestVolumeMetric,
  nearLimit,
} from '../softLimitUsage';

type CardState = {
  kind: SoftLimitKind;
  source: string;
  eyebrow: string;
  title: string;
  body: string;
  finePrint: string | null;
  cta: string;
  dismissLabel: string;
  meta: string | null;
  /** 0..1 para la barra; null si no hay métrica concreta. */
  meter: number | null;
  ratio: number;
  periodMonth: string;
  organizationId: string;
  conversionPlan: 'emprende' | 'crece';
};

export function SoftLimitCreceCard() {
  const navigation = useNavigation<any>();
  const { entitlements } = usePlanEntitlements();
  const [dismissed, setDismissed] = useState<boolean | null>(null);
  const viewTrackedFor = useRef<string | null>(null);

  const state = useMemo<CardState | null>(() => {
    if (!isBillingEnabled() || !entitlements || entitlements.is_owner !== true) return null;
    const plan = (entitlements.plan_code ?? '').toLowerCase();
    if (plan === 'crece') return null;
    const isLegacy = plan === 'legacy';
    const isEmprende = plan === 'emprende' && resolveSubscribedPaidTier(entitlements) === 'emprende';
    if (!isLegacy && !isEmprende && resolveSubscribedPaidTier(entitlements) !== 'gratis') return null;

    const vsl = entitlements.volume_soft_limit;
    const volumeNear = vsl?.near === true;
    const volumeOver = vsl?.over === true;
    const volumeDetail = volumeNear ? hottestVolumeMetric(vsl?.metrics) : null;
    const meter = volumeDetail != null ? Math.min(1, Math.max(0, volumeDetail.ratio)) : null;
    const base = {
      meta: volumeDetail?.meta ?? null,
      meter,
      ratio: volumeDetail?.ratio ?? vsl?.ratio ?? 0,
      periodMonth: vsl?.period_month ?? '',
      organizationId: entitlements.organization_id ?? '',
      conversionPlan: isEmprende ? 'crece' as const : 'emprende' as const,
    };

    if (isLegacy) {
      if (!volumeNear) return null;
      return {
        ...base,
        conversionPlan: 'crece',
        kind: 'legacy_volume',
        source: 'soft_limit_legacy_volume',
        eyebrow: creceLegacyVolumeCopy.eyebrow,
        title: creceLegacyVolumeCopy.title,
        body: creceLegacyVolumeCopy.body,
        finePrint: null,
        cta: creceLegacyVolumeCopy.cta,
        dismissLabel: creceLegacyVolumeCopy.dismiss,
      };
    }

    const structuralNear =
      nearLimit(entitlements.branch_count, entitlements.max_branches) ||
      nearLimit(entitlements.user_count, entitlements.max_users);
    if (!volumeNear && !structuralNear) return null;

    if (volumeNear) {
      const copy = isEmprende ? emprendeVolumeSoftLimitCopy : creceVolumeSoftLimitCopy;
      if (volumeOver) {
        return {
          ...base,
          kind: 'volume_over',
          source: isEmprende ? 'soft_limit_emprende_volume_over' : 'soft_limit_volume_over',
          eyebrow: copy.eyebrow,
          title: copy.titleOver,
          body: copy.bodyOver,
          finePrint: copy.finePrintOver,
          cta: copy.cta,
          dismissLabel: copy.dismiss,
        };
      }
      const percent = volumeDetail != null
        ? Math.min(100, Math.max(0, Math.round(volumeDetail.ratio * 100)))
        : null;
      return {
        ...base,
        kind: 'volume_near',
        source: isEmprende ? 'soft_limit_emprende_volume' : 'soft_limit_volume',
        eyebrow: copy.eyebrow,
        title:
          volumeDetail && volumeDetail.remaining > 0
            ? copy.titleNearRemaining(volumeDetail.remaining, volumeDetail.singular, volumeDetail.plural)
            : copy.titleNear,
        body: percent != null ? copy.bodyNearPercent(percent) : copy.bodyNear,
        finePrint: null,
        cta: copy.cta,
        dismissLabel: copy.dismiss,
      };
    }

    const detail = hottestStructuralUsage(entitlements);
    const atLimit = detail != null && detail.remaining === 0;
    // Cupo estructural al tope (todo gratis 1/1): el pitch real es el tope
    // mensual de volumen. Copy, meter y meta usan las métricas activas del rubro.
    const activeVolume = activeVolumeMetrics(vsl?.metrics);
    const hotVolume = atLimit ? hottestVolumeMetric(activeVolume) : null;
    const meterRatio = atLimit ? hotVolume?.ratio ?? detail?.ratio : detail?.ratio;
    return {
      kind: 'structural',
      source: 'soft_limit',
      eyebrow: creceSoftLimitCopy.eyebrow,
      title: atLimit
        ? creceSoftLimitCopy.titleAtLimit
        : detail != null && detail.remaining > 0
          ? creceSoftLimitCopy.titleNearRemaining(detail.remaining, detail.singular, detail.plural)
          : creceSoftLimitCopy.titleNear,
      body: atLimit
        ? creceSoftLimitCopy.bodyAtLimit(activeVolumeCaps(activeVolume))
        : creceSoftLimitCopy.bodyNear,
      finePrint: null,
      cta: creceSoftLimitCopy.cta,
      dismissLabel: creceSoftLimitCopy.dismiss,
      meta: (atLimit ? hotVolume?.meta : undefined) ?? detail?.meta ?? null,
      meter: meterRatio != null ? Math.min(1, Math.max(0, meterRatio)) : null,
      ratio: meterRatio ?? 0,
      periodMonth: vsl?.period_month ?? '',
      organizationId: entitlements.organization_id ?? '',
      conversionPlan: 'crece',
    };
  }, [entitlements]);

  const dismissKey = state
    ? `${state.organizationId}:${state.periodMonth}:${state.kind}`
    : null;

  useEffect(() => {
    let cancelled = false;
    if (!state) {
      setDismissed(null);
      return;
    }
    setDismissed(null);
    void isSoftLimitDismissed(state.organizationId, state.periodMonth, state.kind)
      .then((v) => {
        if (!cancelled) setDismissed(v);
      })
      .catch(() => {
        if (!cancelled) setDismissed(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissKey]);

  useEffect(() => {
    if (!state || dismissed !== false) return;
    if (viewTrackedFor.current === dismissKey) return;
    viewTrackedFor.current = dismissKey;
    trackCreceFunnel(CRECE_FUNNEL_EVENTS.softLimitView, {
      source: state.source,
      ratio: state.ratio,
      period_month: state.periodMonth,
    });
  }, [state, dismissed, dismissKey]);

  if (!state || dismissed !== false) return null;

  return (
    <View style={styles.card}>
      <Text style={styles.eyebrow}>{state.eyebrow}</Text>
      <Text style={styles.title}>{state.title}</Text>
      <Text style={styles.body}>{state.body}</Text>
      {state.meter != null ? (
        <View
          style={styles.meterTrack}
          accessibilityRole="progressbar"
          accessibilityValue={{ min: 0, max: 100, now: Math.round(state.meter * 100) }}
          testID="soft-limit.meter"
        >
          <View style={[styles.meterFill, { width: `${state.meter * 100}%` }]} />
        </View>
      ) : null}
      {state.meta ? <Text style={styles.meta}>{state.meta}</Text> : null}
      {state.finePrint ? <Text style={styles.finePrint}>{state.finePrint}</Text> : null}
      <TouchableOpacity
        style={styles.primaryBtn}
        onPress={() => {
          trackCreceFunnel(CRECE_FUNNEL_EVENTS.ctaClick, {
            source: state.source,
            plan_code: state.conversionPlan,
          });
          navigateInShell(navigation, 'Billing');
        }}
      >
        <Text style={styles.primaryBtnText}>{state.cta}</Text>
      </TouchableOpacity>
      <TouchableOpacity
        onPress={() => {
          trackCreceFunnel(CRECE_FUNNEL_EVENTS.softLimitDismiss, {
            source: state.source,
            ratio: state.ratio,
            period_month: state.periodMonth,
          });
          void dismissSoftLimit(state.organizationId, state.periodMonth, state.kind);
          setDismissed(true);
        }}
      >
        <Text style={styles.dismiss}>{state.dismissLabel}</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    padding: spacing.md,
    gap: spacing.xs,
    marginBottom: spacing.md,
  },
  eyebrow: {
    fontFamily: ticketFonts.sans,
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
  },
  title: {
    fontFamily: ticketFonts.sans,
    fontWeight: '800',
    fontSize: 16,
    color: ticketColors.ink,
  },
  body: {
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink2,
    lineHeight: 20,
  },
  meterTrack: {
    height: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(28, 25, 23, 0.10)',
    overflow: 'hidden',
    marginTop: spacing.xs,
  },
  meterFill: {
    height: '100%',
    borderRadius: 999,
    backgroundColor: ticketColors.crece,
  },
  meta: {
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    color: ticketColors.ink3,
  },
  finePrint: {
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    color: ticketColors.ink3,
    lineHeight: 16,
  },
  primaryBtn: {
    marginTop: spacing.xs,
    borderRadius: 10,
    backgroundColor: ticketColors.crece,
    paddingVertical: spacing.sm,
    alignItems: 'center',
  },
  primaryBtnText: {
    fontFamily: ticketFonts.sans,
    color: ticketColors.creceInk,
    fontWeight: '800',
  },
  dismiss: {
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink2,
    textAlign: 'center',
    marginTop: spacing.xs,
    fontWeight: '600',
  },
});
